import { useContext, useEffect } from "react";
import { useLocation } from "react-router-dom";
import useQuery from "hooks/useQuery";
import ToolBar from "./ToolBar";
import { FireBaseContext } from "contexts/FireBase";
import { MasterDataContext } from "contexts/MasterDataContext";
import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  ReportType,
  UrlObject,
  breadCrumbLabelsSet,
  formativeDataSet,
  generationTimeSet,
  isFormativeLoadingSet,
  isProgressLoadingSet,
  progressDataSet,
  reportTypeSet,
  selectBreadCrumbLabels,
  selectGenerationTime,
  selectReportType,
  selectSelectedClassCode,
  selectSelectedGradeCode,
  selectSelectedRange,
  selectSelectedTeacherCode,
  selectedClassCodeSet,
  selectedGradeCodeSet,
  selectedTeacherCodeSet,
} from "features/Reports";
import { Grid, Typography } from "@mui/material";
import ProgressSection, { getProgressReport } from "./Progress";
import FormativeSection, { getFormativeReport } from "./Formative";
import ReportsBreadcrumb from "./ToolBar/ReportsBreadcrumb";
import {
  IProgressClassResponse,
  IProgressGradeResponse,
  IProgressTeacherResponse,
  useFetchFormativeClassQuery,
  useFetchFormativeGradeQuery,
  useFetchFormativeGradesQuery,
  useFetchFormativeTeacherQuery,
  useFetchFormativeTeachersQuery,
  useFetchProgressClassQuery,
  useFetchProgressGradeQuery,
  useFetchProgressGradesQuery,
  useFetchProgressTeacherQuery,
  useFetchProgressTeachersQuery,
} from "app/services/reports";
import {
  getLabelAndTitle,
  getProgressDataKeys,
  translateProgressData,
} from "./Utils/ProgressHelpers";
import { useTranslation } from "react-i18next";

const customStyles = {
  root: {
    margin: "20px 0",
    borderRadius: "2px",
    boxShadow: "0 2px 4px 0 rgba(179, 187, 199, 1)",
    backgroundColor: "rgb(255, 255, 255)",
    "@media print": {
      margin: 0,
      boxShadow: "none",
      position: "absolute",
    },
  },
  progress: {
    padding: "2rem 0.75rem",
    "@media print": {
      width: "100%",
      breakInside: "avoid",
      pageBreakBefore: "always",
      pageBreakAfter: "always",
    },
  },
  formative: {
    borderTop: "1px solid rgba(237, 241, 247, 1)",
    padding: "2rem 0.75rem",
    "@media print": {
      border: "none",
      width: "100%",
      breakInside: "avoid",
      pageBreakBefore: "always",
      pageBreakAfter: "always",
    },
  },
  reportDate: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    flexDirection: "row",
    padding: "3px",
    border: "1px solid #fff",
    "& > p": {
      padding: "5px",
      textAlign: "center",
      fontSize: "12px",
      fontWeight: "bold",
      color: "rgb(0, 130, 183)",
      background: "rgb(200, 238, 255)",
      borderRadius: "1px",
    },
  },
};

export const updateBreadCrumb = (
  breadCrumb: string[],
  reportType: ReportType,
  dispatch: ReturnType<typeof useAppDispatch>,
  selectedTeacherCode?: string,
  selectedGradeCode?: string,
  progressTeacherData?: IProgressTeacherResponse,
  progressClassData?: IProgressClassResponse,
  progressGradeData?: IProgressGradeResponse
) => {
  let breadCrumbLabels = [...breadCrumb];
  switch (reportType) {
    case ReportType.Teacher_Report:
      if (
        progressTeacherData?._statusCode === 200 &&
        progressTeacherData?._entity
      ) {
        const { _classProgressList: classes, _teacherName: teacherName } =
          progressTeacherData._entity;
        if (classes?.length) {
          const teacher = getLabelAndTitle(teacherName).title;
          breadCrumbLabels.splice(1);
          breadCrumbLabels.push(teacher);
          dispatch(breadCrumbLabelsSet(breadCrumbLabels));
        }
      }
      break;
    case ReportType.Class_Report:
      if (
        progressClassData?._statusCode === 200 &&
        progressClassData?._entity
      ) {
        const {
          _studentProgressList: students,
          _teacherName: teacherName,
          _className: classTitle,
        } = progressClassData._entity;

        if (students?.length) {
          breadCrumbLabels.splice(1);
          const teacher = getLabelAndTitle(teacherName).title;
          if (selectedTeacherCode)
            breadCrumbLabels.push.apply(breadCrumbLabels, [
              teacher,
              classTitle,
            ]);
          else
            breadCrumbLabels.push.apply(breadCrumbLabels, [
              selectedGradeCode!,
              classTitle,
            ]);
          dispatch(breadCrumbLabelsSet(breadCrumbLabels));
        }
      }
      break;
    case ReportType.Grade_Report:
      if (
        progressGradeData?._statusCode === 200 &&
        progressGradeData?._entity
      ) {
        breadCrumbLabels.splice(1);
        breadCrumbLabels.push(selectedGradeCode!);
        dispatch(breadCrumbLabelsSet(breadCrumbLabels));
      }
      break;
    default:
      dispatch(breadCrumbLabelsSet([reportType]));
  }
};

function Content() {
  const { i18n, t } = useTranslation();
  let { pathname: url } = useLocation();
  const dispatch = useAppDispatch();
  const breadCrumbLabels = useAppSelector(selectBreadCrumbLabels);
  const reportType = useAppSelector(selectReportType);
  const selectedRange = useAppSelector(selectSelectedRange);
  const selectedClassCode = useAppSelector(selectSelectedClassCode);
  const selectedTeacherCode = useAppSelector(selectSelectedTeacherCode);
  const selectedGradeCode = useAppSelector(selectSelectedGradeCode);
  const breadCrumb = useAppSelector(selectBreadCrumbLabels);
  const generationTime = useAppSelector(selectGenerationTime);

  const {
    state: { schoolDetails, gradeComponentMounted, teacherComponentMounted },
    dispatch: masterDispatch,
  } = useContext(MasterDataContext);

  const { logEvent } = useContext(FireBaseContext);
  let query = useQuery();
  const teacherCode = query.get("teacherCode") ?? undefined;
  const classCode = query.get("classCode") ?? undefined;
  const gradeCode = query.get("gradeCode") ?? undefined;
  let newUrl = url.substring(1);

  useEffect(() => {
    if (
      newUrl === UrlObject.GradesUrl &&
      Object.keys(schoolDetails).length &&
      !gradeComponentMounted
    ) {
      logEvent("VisitAbjadiyatHODGradesReport");
      masterDispatch({ type: "gradeComponentMounted", payload: true });
    } else if (Object.keys(schoolDetails).length && !teacherComponentMounted) {
      logEvent("VisitAbjadiyatHODTeachersReport");
      masterDispatch({ type: "teacherComponentMounted", payload: true });
    }
  }, [schoolDetails]);

  useEffect(() => {
    let labelsArray = [...breadCrumbLabels];
    labelsArray.length = 0;
    let labelName = "Teachers";
    if (newUrl === UrlObject.GradesUrl) labelName = "Grades";
    labelsArray.push(labelName);
    setReportType(newUrl);
    dispatch(breadCrumbLabelsSet(labelsArray));
    dispatch(selectedTeacherCodeSet(teacherCode));
    dispatch(selectedClassCodeSet(classCode));
    dispatch(selectedGradeCodeSet(gradeCode));
  }, [url, query.toString()]);

  const setReportType = (newUrl: string) => {
    let repoType;
    if ((teacherCode || gradeCode) && classCode)
      repoType = ReportType.Class_Report;
    else if (teacherCode) repoType = ReportType.Teacher_Report;
    else if (gradeCode) repoType = ReportType.Grade_Report;
    else {
      repoType =
        newUrl === UrlObject.GradesUrl
          ? ReportType.Grades_Report
          : ReportType.Teachers_Report;
    }
    dispatch(reportTypeSet(repoType));
  };

  const skipFetchingClassData =
    reportType !== ReportType.Class_Report || !selectedClassCode;
  const skipFetchingTeacherData =
    reportType !== ReportType.Teacher_Report || !selectedTeacherCode;
  const skipFetchingGradeData =
    reportType !== ReportType.Grade_Report || !selectedGradeCode;
  const skipFetchingTeachersData = reportType !== ReportType.Teachers_Report;
  const skipFetchingGradesData = reportType !== ReportType.Grades_Report;

  const {
    data: progressTeachersData,
    isLoading: isProgressTeachersDataLoading,
  } = useFetchProgressTeachersQuery(selectedRange.value, {
    skip: skipFetchingTeachersData,
  });

  const { data: progressTeacherData, isLoading: isProgressTeacherDataLoading } =
    useFetchProgressTeacherQuery(
      { period: selectedRange.value, teacher: selectedTeacherCode! },
      { skip: skipFetchingTeacherData }
    );
  const { data: progressClassData, isLoading: isProgressClassDataLoading } =
    useFetchProgressClassQuery(
      { period: selectedRange.value, classId: selectedClassCode! },
      { skip: skipFetchingClassData }
    );

  const { data: progressGradesData, isLoading: isProgressGradesDataLoading } =
    useFetchProgressGradesQuery(selectedRange.value, {
      skip: skipFetchingGradesData,
    });

  const { data: progressGradeData, isLoading: isProgressGradeDataLoading } =
    useFetchProgressGradeQuery(
      { period: selectedRange.value, grade: selectedGradeCode! },
      { skip: skipFetchingGradeData }
    );

  const isFetchingProgressData =
    isProgressTeachersDataLoading ||
    isProgressClassDataLoading ||
    isProgressTeacherDataLoading ||
    isProgressGradesDataLoading ||
    isProgressGradeDataLoading;

  useEffect(() => {
    if (isFetchingProgressData) dispatch(isProgressLoadingSet(true));
    else dispatch(isProgressLoadingSet(false));
  }, [isFetchingProgressData]);

  useEffect(() => {
    const isProgressDataFetched =
      progressTeachersData ||
      progressTeacherData ||
      progressClassData ||
      progressGradeData ||
      progressGradesData;
    if (isProgressDataFetched) {
      const data = getProgressReport(
        reportType,
        progressTeachersData,
        progressTeacherData,
        progressClassData,
        progressGradesData,
        progressGradeData
      );
      const translatedProgressData = translateProgressData(
        data ?? [],
        reportType
      );
      const progressDataKeys = getProgressDataKeys(reportType, t);
      dispatch(
        progressDataSet({
          chartData: translatedProgressData,
          dataKeys: progressDataKeys,
        })
      );
      updateBreadCrumb(
        breadCrumb,
        reportType,
        dispatch,
        selectedTeacherCode,
        selectedGradeCode,
        progressTeacherData,
        progressClassData,
        progressGradeData
      );
    }
  }, [
    progressTeachersData,
    progressTeacherData,
    progressClassData,
    progressGradeData,
    progressGradesData,
    reportType,
    selectedRange,
    i18n.dir(),
  ]);

  const {
    data: formativeTeachersData,
    isLoading: isFormativeTeachersDataLoading,
  } = useFetchFormativeTeachersQuery(selectedRange.value, {
    skip: skipFetchingTeachersData,
  });
  const {
    data: formativeTeacherData,
    isLoading: isFormativeTeacherDataLoading,
  } = useFetchFormativeTeacherQuery(
    { period: selectedRange.value, teacher: selectedTeacherCode! },
    { skip: skipFetchingTeacherData }
  );

  const { data: formativeClassData, isLoading: isFormativeClassDataLoading } =
    useFetchFormativeClassQuery(
      { period: selectedRange.value, classId: selectedClassCode! },
      { skip: skipFetchingClassData }
    );

  const { data: formativeGradesData, isLoading: isFormativeGradesDataLoading } =
    useFetchFormativeGradesQuery(selectedRange.value, {
      skip: skipFetchingGradesData,
    });

  const { data: formativeGradeData, isLoading: isFormativeGradeDataLoading } =
    useFetchFormativeGradeQuery(
      { period: selectedRange.value, grade: selectedGradeCode! },
      { skip: skipFetchingGradeData }
    );

  const isFetchingFormativeData =
    isFormativeTeachersDataLoading ||
    isFormativeClassDataLoading ||
    isFormativeTeacherDataLoading ||
    isFormativeGradesDataLoading ||
    isFormativeGradeDataLoading;

  useEffect(() => {
    if (isFetchingFormativeData) dispatch(isFormativeLoadingSet(true));
    else dispatch(isFormativeLoadingSet(false));
  }, [isFetchingFormativeData]);

  useEffect(() => {
    const isFormativeDataFetched =
      formativeTeachersData ||
      formativeTeacherData ||
      formativeClassData ||
      formativeGradeData ||
      formativeGradesData;
    if (isFormativeDataFetched) {
      const { dataList, reportDate } = getFormativeReport(
        reportType,
        formativeTeachersData,
        formativeTeacherData,
        formativeClassData,
        formativeGradesData,
        formativeGradeData
      );
      dispatch(formativeDataSet(dataList));
      if (reportDate) dispatch(generationTimeSet(reportDate));
    }
  }, [
    formativeTeachersData,
    formativeTeacherData,
    formativeClassData,
    formativeGradesData,
    formativeGradeData,
    reportType,
    selectedRange,
    i18n.dir(),
  ]);
  return (
    <>
      <ReportsBreadcrumb />
      <ToolBar />
      <Grid container sx={customStyles.root} direction="column">
        {generationTime ? (
          <Grid item xs={12} sx={customStyles.reportDate}>
            <Typography component="div" sx={{ flexGrow: 1 }}></Typography>
            <Typography component="p">{`${t(
              "Report Date:"
            )} ${generationTime}`}</Typography>
          </Grid>
        ) : null}
        <Grid item xs={12} sx={customStyles.progress}>
          <ProgressSection />
        </Grid>
        <Grid item xs={12} sx={customStyles.formative}>
          <FormativeSection />
        </Grid>
      </Grid>
    </>
  );
}

export default Content;
